import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../Context/UserContext/index.tsx';
import './style.css';

const Sidebar = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [active, setActive] = useState('');
  const [mcqAccessed, setMcqAccessed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0); // in seconds

  const navigate = useNavigate();
  const { courseId, sts, examStatus, canName, nric } = useAuth();

  // Detect if admin mode is active
  useEffect(() => {
    const adminData = localStorage.getItem('adminData');
    if (adminData) {
      setIsAdmin(true);
    }
  }, []);

  // Fetch course menu if not admin
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const res = await axios.get(`https://elearn-api.onrender.com/api/course-menu/${courseId}`);
        const courseTitles = res.data.map(item => item.CourseMenu.trim());
        setMenuItems(courseTitles);
        setActive(courseTitles[0] || '');
      } catch (error) {
        console.error('Error fetching menu:', error);
      }
    };

    if (courseId && !isAdmin) fetchMenu();
  }, [courseId, isAdmin]);

  // Initialize login start time
  useEffect(() => {
    const adminData = localStorage.getItem('adminData');
    const loginStart = localStorage.getItem('loginStartTime');

    if (!adminData && !loginStart) {
      const now = Date.now();
      localStorage.setItem('loginStartTime', now.toString());
      setTimeElapsed(0); // start timer from 0
    } else if (loginStart) {
      const start = parseInt(loginStart);
      const secondsElapsed = Math.floor((Date.now() - start) / 1000);
      setTimeElapsed(secondsElapsed);
    }
  }, []);

  // Live timer effect
  useEffect(() => {
    const adminData = localStorage.getItem('adminData');
    if (adminData) return; // don't run timer for admin

    const interval = setInterval(() => {
      setTimeElapsed(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const isDisabled = (index) => {
    const currentSts = Number(sts);
    const isLast = index === menuItems.length - 1;
    const isSecondLast = index === menuItems.length - 2;

    if (isLast) return !(mcqAccessed && examStatus === 'Pass');
    if (currentSts === 0) return index === 0;
    return index > currentSts;
  };

  const handleClick = (item, linkTo, isDisabled, index) => {
    if (!isDisabled) {
      setActive(item);
      navigate(linkTo);
      if (index === menuItems.length - 2) setMcqAccessed(true);
    }
  };

  // Convert seconds to HH:MM:SS
  const formatTime = (secs) => {
    const hours = Math.floor(secs / 3600);
    const minutes = Math.floor((secs % 3600) / 60);
    const seconds = secs % 60;
    return `${hours.toString().padStart(2,'0')}:${minutes.toString().padStart(2,'0')}:${seconds.toString().padStart(2,'0')}`;
  };

  return (
    <div className="sidebar">
      {/* Top-right timer */}
      {!isAdmin && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '20px',
          fontWeight: 'bold',
          fontSize: '16px',
          background: '#f0f0f0',
          padding: '5px 10px',
          borderRadius: '5px'
        }}>
          Time: {formatTime(timeElapsed)}
        </div>
      )}

      {!isAdmin ? (
        <ul className="menu">
          {menuItems.map((text, index) => {
            const moduleMatch = text.match(/^Module (\d+)$/);
            const mcqMatch = text.match(/^MCQ (\d+)$/);

            let linkTo = '#';
            if (moduleMatch) linkTo = `/video/${moduleMatch[1]}`;
            else if (mcqMatch) linkTo = `/mcq/${mcqMatch[1]}`;
            else {
              switch (text) {
                case 'Declaration': linkTo = '/attendance'; break;
                case 'MCQ Report': linkTo = '/mcq-summary'; break;
                case 'Course Info': linkTo = '/course-info'; break;
                case 'Report': linkTo = '/report'; break;
                case 'Download Certificate': linkTo = '/cert-download'; break;
              }
            }

            const disabled = isDisabled(index);

            return (
              <li
                key={index}
                className={`${active === text ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => handleClick(text, linkTo, disabled, index)}
                style={{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                {text}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="course-btn-wrapper">
          <div onClick={() => navigate("/admin-home")} className="Course-btn">Home</div>
          <div onClick={() => navigate("/add-course-new")} className="Course-btn">Add Course</div>
          <div onClick={() => navigate("/add-course")} className="Course-btn">Add Class</div>
          <div onClick={() => navigate("/add-student")} className="Course-btn">Add Student</div>
          <div onClick={() => navigate("/add-question")} className="Course-btn">Add Question</div>
          <div onClick={() => navigate("/admin")} className="Course-btn">Download Certificate</div>
          <div className="Course-btn">Logs Report</div>
          <div className="Course-btn">Add Video</div>
          <div onClick={() => navigate("/summary-admin")} className="Course-btn">Summary Report</div>
        </div>
      )}

      {/* Logout button */}
      <div
        className="log-out-btn"
        onClick={async () => {
          const adminData = localStorage.getItem('adminData');
          const loginStart = localStorage.getItem('loginStartTime');

          if (!adminData && canName && nric && loginStart) {
            const startTime = parseInt(loginStart);
            const now = Date.now();
            const timeSpent = Math.floor((now - startTime) / 60000); // in minutes

            try {
              await axios.post('https://elearn-api.onrender.com/api/updateTimeSpent', {
                Name: canName,
                NricId: nric,
                TimeSpent: timeSpent,
              });
              console.log('TimeSpent updated:', timeSpent, 'mins');
            } catch (error) {
              console.error('Error updating timeSpent:', error);
            }
          }

          localStorage.removeItem('adminData');
          localStorage.removeItem('userData');
          localStorage.removeItem('loginStartTime');

          navigate('/');
        }}
      >
        Log out
      </div>
    </div>
  );
};

export default Sidebar;
